// Estado del formulario de un agente IA (helpdesk) y su traducción al payload
// del contrato `/api/v1/helpdesk/ai-agents`.
//
// Puro: sin React, sin fetch, sin i18n. Acá viven las reglas del contrato —qué
// viaja, qué NO viaja y qué se valida antes de enviar— para que los tests las
// cubran sin montar la vista.
//
// Dos decisiones que se ven en todo el módulo:
//  - Los numéricos del formulario son STRING (es lo que el usuario tipea en un
//    input) y el payload los sanea a entero; en `limits`, 0 = sin límite.
//  - El formulario arranca con `baseline` (el estado tal como llegó del GET) y
//    el PUT es PARCIAL: solo viajan las secciones que cambiaron. Sin baseline
//    (agente nuevo) viaja el documento completo.
//
// SEGURIDAD: el token del proveedor solo entra al payload si el llamador pasa
// `typedToken` (lo que el usuario acaba de tipear). El estado NUNCA lo guarda:
// del backend solo llegan has_token/token_last4.

import { CUSTOM_PROVIDER_ID, recommendedModelId } from "./aiModelCatalog";
import { initialScopeStates, buildAccountOverrides } from "./aiScope";

// Topes del contrato (espejo de los dto del backend).
export const AGENT_NAME_MAX = 60;
export const AGENT_DESCRIPTION_MAX = 300;
export const AGENT_AVATAR_MAX = 16;
export const AGENT_INSTRUCTIONS_MAX = 6000;
export const AGENT_KB_INSTRUCTIONS_MAX = 2000;
export const AGENT_ESCALATION_MAX = 300;
export const AGENT_PRIORITY_MAX = 100;
export const AGENT_MAX_REPLIES_CAP = 50;
// Ventana de "ceder al humano" en horas (AI_HANDOFF_WINDOW_MAX_HOURS: 7 días).
// 0 = cualquier respuesta humana del caso (el comportamiento anterior).
export const AGENT_HANDOFF_WINDOW_MAX = 168;
// Firma de los mensajes de la IA (style.assistant_name): mismo tope y mismo
// alfabeto que el dto del backend (letras de cualquier idioma, espacios y
// . - '). Vacío = sin firma.
export const AGENT_ASSISTANT_NAME_MAX = 40;
const ASSISTANT_NAME_PATTERN = /^[\p{L} .'-]*$/u;
// Largo del token de proveedor: por debajo del mínimo no es una credencial (es
// un dedazo) y por encima del máximo no es un token (es un pegado de más).
export const AGENT_TOKEN_MIN = 10;
export const AGENT_TOKEN_MAX = 500;

const AGENT_DEFAULT_PRIORITY = 50;
const AGENT_DEFAULT_AVATAR = "🤖";
// Proveedor por defecto cuando el agente NO hereda el del courier: el
// recomendado del contrato (OpenAI-compatible, económico) y su modelo vigente.
export const AGENT_DEFAULT_KIND = "deepseek";

export const AGENT_STATUSES = ["active", "paused", "draft"];
export const AGENT_AUDIENCES = ["customer", "merchant", "driver", "internal"];
export const AGENT_TRIGGERS = ["always", "off_hours", "first_reply"];
// Perillas del motor: razonamiento (solo tiene efecto con DeepSeek) y
// autoaprendizaje del agente desde las consultas al equipo.
export const AGENT_REASONING_MODES = ["off", "on"];
export const AGENT_SELF_LEARNING_MODES = ["off", "review", "auto"];

/**
 * Destino efectivo de un endpoint OpenAI-compatible, con la MISMA
 * normalización que el backend (normalizeOpenAiCompatBaseUrl): sin espacios ni
 * barras finales y con /chat/completions al final. Dos URLs con el mismo
 * destino no invalidan el token guardado.
 */
export function customEndpointTarget(baseUrl) {
  const trimmed = String(baseUrl || "").trim().replace(/\/+$/, "");
  if (!trimmed) return "";
  return trimmed.endsWith("/chat/completions") ? trimmed : `${trimmed}/chat/completions`;
}

// Perillas de personalización (capa 1). Las listas de valores válidos viven acá
// (son contrato); las etiquetas visibles, en agentSections/primitives.jsx.
export const STYLE_DEFAULTS = {
  style_tone: "profesional",
  style_warmth: "normal",
  style_emoji: "pocos",
  style_length: "breve",
  style_address: "tu",
  // Texto libre, no perilla: fuera de STYLE_VALUES. "" = el agente no firma.
  assistant_name: "",
};

export const STYLE_VALUES = {
  style_tone: ["profesional", "cercano", "formal"],
  style_warmth: ["menos", "normal", "mas"],
  style_emoji: ["no", "pocos", "si"],
  style_length: ["breve", "normal", "detallado"],
  style_address: ["tu", "usted"],
};

const BEHAVIOR_DEFAULTS = {
  trigger: "always",
  business_days: [1, 2, 3, 4, 5],
  business_start: "09:00",
  business_end: "18:00",
  max_replies_per_case: "0",
  handoff_on_agent_reply: true,
  handoff_window_hours: "0",
  escalation_message: "",
  reasoning: "off",
  self_learning: "off",
};

const LIMITS_DEFAULTS = { max_daily_actions: "0", max_daily_tokens: "0" };

// Claves planas del documento: viajan sueltas (no forman sección).
const IDENTITY_KEYS = ["name", "description", "avatar", "status", "audience", "priority"];
// Secciones con merge profundo en el backend: se mandan completas cuando algo
// de la sección cambió, y se omiten por entero cuando no se tocaron.
const SECTION_KEYS = ["scope", "provider", "prompt", "style", "behavior", "capabilities", "limits"];

// ─── Helpers internos ────────────────────────────────────────────────────

const asString = (value, fallback = "") => (typeof value === "string" ? value : fallback);

const clone = (value) => JSON.parse(JSON.stringify(value === undefined ? null : value));

// Entero saneado: input vacío/NaN cae al mínimo (0 en todo el contrato).
function toInt(value, min = 0, max = Number.MAX_SAFE_INTEGER) {
  const n = parseInt(value, 10);
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, n));
}

/**
 * Como toInt pero sin truncar: la ventana de traspaso se configura en horas y
 * admite fracciones (0.5 = media hora). Redondea a dos decimales, que es lo que
 * acepta el backend.
 */
function toDecimal(value, min = 0, max = Number.MAX_SAFE_INTEGER) {
  const n = parseFloat(value);
  if (!Number.isFinite(n)) return min;
  const clamped = Math.min(max, Math.max(min, n));
  return Math.round(clamped * 100) / 100;
}

const idList = (value) =>
  (Array.isArray(value) ? value : []).filter(
    (id, i, all) => typeof id === "string" && id && all.indexOf(id) === i,
  );

// La lista de canales llega del backend (prop `scope`): puede venir como array
// o envuelta en { channels }. El front NUNCA inventa claves: si no hay lista,
// no hay nada que togglear ni que guardar.
function channelList(scope) {
  if (Array.isArray(scope)) return scope;
  if (Array.isArray(scope?.channels)) return scope.channels;
  return [];
}

// Audiencias sin canal propio (repartidores, uso interno): un agente suyo NO
// arranca encendido en ningún lado — el admin enciende lo que corresponda, que
// es justo lo que advierte el hint de esas audiencias.
const audienceStartsOff = (audience) => audience === "driver" || audience === "internal";

/**
 * Estado editable del alcance (canales y cuentas) de un agente.
 *
 * Un canal sin valor propio en el documento arranca en el default de la
 * audiencia: espejo del alcance del courier (initialScopeStates) para
 * customer/merchant, y apagado para repartidores/internos. Una cuenta sin
 * excepción propia sigue a su canal — mismo modelo mental que aiScope.js.
 *
 * @param {Array|object} scope lista de canales del tenant
 * @param {string} audience
 * @param {object} [toggles] channel_toggles del agente
 * @param {object} [overrides] account_overrides del agente, ya en mapa key→bool
 * @returns {{channels: Array, channelStates: object, accountStates: object}}
 */
function buildScopeState(scope, audience = "customer", toggles = {}, overrides = {}) {
  const channels = channelList(scope);
  const tenantStates = initialScopeStates(channels);
  const off = audienceStartsOff(audience);

  const channelStates = {};
  channels.forEach((c) => {
    const explicit = toggles?.[c.channel];
    if (typeof explicit === "boolean") channelStates[c.channel] = explicit;
    else channelStates[c.channel] = off ? false : tenantStates.channelStates[c.channel] !== false;
  });

  const accountStates = {};
  channels.forEach((c) => {
    (c.accounts || []).forEach((a) => {
      const explicit = overrides?.[a.key];
      if (typeof explicit === "boolean") accountStates[a.key] = explicit;
      else accountStates[a.key] = off ? false : channelStates[c.channel];
    });
  });

  return { channels, channelStates, accountStates };
}

// Estado sin el baseline: lo que se compara contra el GET para decidir qué
// secciones viajan en el PUT parcial.
function snapshot(state) {
  const { baseline, ...rest } = state || {};
  return clone(rest);
}

/**
 * Estado inicial de un agente NUEVO, con los defaults del contrato.
 * `inherit_tenant` arranca en true si el courier ya tiene token propio (sin él,
 * el agente nace con proveedor y modelo recomendados) o si el rol no administra
 * el proveedor: ese rol no manda la sección y el backend crea el agente
 * heredando el del courier, así que el formulario muestra eso desde el inicio.
 * @param {object} catalog catálogo (providers/audiences/capabilities)
 * @param {object} tenantSettings ai-settings del courier (acepta {settings} o el settings pelado)
 * @param {Array|object} [scope] canales del tenant
 * @param {string} [audience] audiencia preseleccionada (p. ej. desde la URL);
 *   un valor fuera del contrato cae a "customer" en vez de viajar al backend
 * @param {{canManageProvider?: boolean}} [options] permisos del llamador (sin
 *   el dato, permitido: el contrato es aditivo)
 * @returns {object}
 */
export function defaultAgentState(
  catalog,
  tenantSettings,
  scope = [],
  audience = "customer",
  { canManageProvider = true } = {},
) {
  const providers = Array.isArray(catalog?.providers) ? catalog.providers : [];
  const tenant = tenantSettings?.settings || tenantSettings || {};
  const inherit = tenant.has_token === true || canManageProvider === false;
  const aud = AGENT_AUDIENCES.includes(audience) ? audience : "customer";
  // Proveedor/modelo por defecto: deepseek o, si el catálogo no lo trae, el
  // primero con modelos. Se eligen SIEMPRE (aunque el agente herede) para que
  // apagar la herencia muestre una selección válida en vez de un select vacío.
  const defaultProvider =
    providers.find((p) => p.id === AGENT_DEFAULT_KIND) ||
    providers.find((p) => (p.models || []).length > 0) ||
    null;

  return {
    name: "",
    description: "",
    avatar: AGENT_DEFAULT_AVATAR,
    status: "draft",
    audience: aud,
    priority: String(AGENT_DEFAULT_PRIORITY),
    scope: buildScopeState(scope, aud),
    provider: {
      inherit_tenant: inherit,
      kind: defaultProvider?.id || AGENT_DEFAULT_KIND,
      base_url: "",
      model: recommendedModelId(defaultProvider),
      has_token: false,
      token_last4: "",
    },
    prompt: { instructions: "", knowledge_base: [], kb_instructions: "" },
    style: { ...STYLE_DEFAULTS },
    behavior: { ...BEHAVIOR_DEFAULTS, business_days: [...BEHAVIOR_DEFAULTS.business_days] },
    capabilities: { read: [], write: [] },
    limits: { ...LIMITS_DEFAULTS },
    baseline: null,
  };
}

/**
 * Estado editable a partir del documento que devuelve el backend.
 * @param {object} agent documento del contrato
 * @param {Array|object} [scope] canales del tenant
 * @returns {object} estado con `baseline` = copia del propio estado
 */
export function agentToState(agent, scope = []) {
  const a = agent || {};
  const provider = a.provider || {};
  const prompt = a.prompt || {};
  const behavior = a.behavior || {};
  const limits = a.limits || {};
  const capabilities = a.capabilities || {};
  const audience = AGENT_AUDIENCES.includes(a.audience) ? a.audience : "customer";

  const toggles = a.scope?.channel_toggles || {};
  const overrideMap = {};
  (Array.isArray(a.scope?.account_overrides) ? a.scope.account_overrides : []).forEach((o) => {
    if (o && typeof o.key === "string") overrideMap[o.key] = o.enabled === true;
  });

  const style = {};
  Object.entries(STYLE_VALUES).forEach(([key, values]) => {
    const value = a.style?.[key];
    style[key] = values.includes(value) ? value : STYLE_DEFAULTS[key];
  });
  // Un agente guardado antes de que existiera la firma se lee como "" (sin
  // firma), igual que el default: así no aparece como cambio en el PUT parcial.
  style.assistant_name = asString(a.style?.assistant_name);

  const state = {
    name: asString(a.name),
    description: asString(a.description),
    avatar: asString(a.avatar).trim() || AGENT_DEFAULT_AVATAR,
    status: AGENT_STATUSES.includes(a.status) ? a.status : "draft",
    audience,
    priority: String(toInt(a.priority, 0, AGENT_PRIORITY_MAX)),
    scope: buildScopeState(scope, audience, toggles, overrideMap),
    provider: {
      inherit_tenant: provider.inherit_tenant === true,
      kind: asString(provider.kind) || AGENT_DEFAULT_KIND,
      base_url: asString(provider.base_url),
      model: asString(provider.model),
      has_token: provider.has_token === true,
      token_last4: asString(provider.token_last4),
    },
    prompt: {
      instructions: asString(prompt.instructions),
      knowledge_base: Array.isArray(prompt.knowledge_base) ? clone(prompt.knowledge_base) : [],
      kb_instructions: asString(prompt.kb_instructions),
    },
    style,
    behavior: {
      trigger: AGENT_TRIGGERS.includes(behavior.trigger) ? behavior.trigger : "always",
      business_days: Array.isArray(behavior.business_days)
        ? [...new Set(behavior.business_days.filter((d) => Number.isInteger(d)))].sort((x, y) => x - y)
        : [...BEHAVIOR_DEFAULTS.business_days],
      business_start: asString(behavior.business_start) || BEHAVIOR_DEFAULTS.business_start,
      business_end: asString(behavior.business_end) || BEHAVIOR_DEFAULTS.business_end,
      max_replies_per_case: String(
        toInt(behavior.max_replies_per_case, 0, AGENT_MAX_REPLIES_CAP),
      ),
      handoff_on_agent_reply: behavior.handoff_on_agent_reply !== false,
      // Un agente guardado antes de que existiera el campo se lee como 0.
      handoff_window_hours: String(
        toDecimal(behavior.handoff_window_hours, 0, AGENT_HANDOFF_WINDOW_MAX),
      ),
      escalation_message: asString(behavior.escalation_message),
      // Un agente guardado antes de que existieran las perillas se lee como
      // "off" (el default del contrato), así no aparece como cambio en el PUT.
      reasoning: AGENT_REASONING_MODES.includes(behavior.reasoning) ? behavior.reasoning : "off",
      self_learning: AGENT_SELF_LEARNING_MODES.includes(behavior.self_learning)
        ? behavior.self_learning
        : "off",
    },
    capabilities: { read: idList(capabilities.read), write: idList(capabilities.write) },
    limits: {
      max_daily_actions: String(toInt(limits.max_daily_actions, 0)),
      max_daily_tokens: String(toInt(limits.max_daily_tokens, 0)),
    },
    baseline: null,
  };

  state.baseline = snapshot(state);
  return state;
}

// Documento del contrato a partir del estado. `scope` queda en null cuando no
// hay lista de canales (ruta del alcance caída): mandar {} apagaría el agente
// en todos lados.
function toContract(state) {
  const s = state || {};
  const provider = s.provider || {};
  const inherit = provider.inherit_tenant === true;
  const kind = asString(provider.kind) || AGENT_DEFAULT_KIND;
  const { channels, channelStates, accountStates } = s.scope || {};

  const scope =
    Array.isArray(channels) && channels.length > 0
      ? {
          channel_toggles: { ...channelStates },
          account_overrides: buildAccountOverrides(channels, channelStates, accountStates),
        }
      : null;

  return {
    name: asString(s.name).trim(),
    description: asString(s.description).trim(),
    avatar: asString(s.avatar).trim() || AGENT_DEFAULT_AVATAR,
    status: AGENT_STATUSES.includes(s.status) ? s.status : "draft",
    audience: AGENT_AUDIENCES.includes(s.audience) ? s.audience : "customer",
    priority: toInt(s.priority, 0, AGENT_PRIORITY_MAX),
    scope,
    provider: inherit
      ? // Heredar es todo-o-nada: sin kind/model/base_url, el agente usa el
        // proveedor del courier tal como esté configurado.
        { inherit_tenant: true }
      : {
          inherit_tenant: false,
          kind,
          // base_url SOLO para el endpoint propio del contrato.
          ...(kind === CUSTOM_PROVIDER_ID
            ? { base_url: asString(provider.base_url).trim() }
            : {}),
          model: asString(provider.model).trim(),
        },
    prompt: {
      instructions: asString(s.prompt?.instructions),
      knowledge_base: Array.isArray(s.prompt?.knowledge_base) ? clone(s.prompt.knowledge_base) : [],
      kb_instructions: asString(s.prompt?.kb_instructions),
    },
    style: {
      ...STYLE_DEFAULTS,
      ...(s.style || {}),
      // El backend recorta al guardar; se manda ya recortado para que lo
      // validado acá sea exactamente lo que se guarda.
      assistant_name: asString(s.style?.assistant_name).trim(),
    },
    behavior: {
      trigger: AGENT_TRIGGERS.includes(s.behavior?.trigger) ? s.behavior.trigger : "always",
      business_days: Array.isArray(s.behavior?.business_days)
        ? [...s.behavior.business_days].sort((a, b) => a - b)
        : [...BEHAVIOR_DEFAULTS.business_days],
      business_start: asString(s.behavior?.business_start) || BEHAVIOR_DEFAULTS.business_start,
      business_end: asString(s.behavior?.business_end) || BEHAVIOR_DEFAULTS.business_end,
      max_replies_per_case: toInt(
        s.behavior?.max_replies_per_case,
        0,
        AGENT_MAX_REPLIES_CAP,
      ),
      handoff_on_agent_reply: s.behavior?.handoff_on_agent_reply !== false,
      handoff_window_hours: toDecimal(
        s.behavior?.handoff_window_hours,
        0,
        AGENT_HANDOFF_WINDOW_MAX,
      ),
      escalation_message: asString(s.behavior?.escalation_message),
      reasoning: AGENT_REASONING_MODES.includes(s.behavior?.reasoning)
        ? s.behavior.reasoning
        : "off",
      self_learning: AGENT_SELF_LEARNING_MODES.includes(s.behavior?.self_learning)
        ? s.behavior.self_learning
        : "off",
    },
    capabilities: { read: idList(s.capabilities?.read), write: idList(s.capabilities?.write) },
    limits: {
      max_daily_actions: toInt(s.limits?.max_daily_actions, 0),
      max_daily_tokens: toInt(s.limits?.max_daily_tokens, 0),
    },
  };
}

/**
 * Marca el estado actual como ya guardado (baseline = sí mismo). Se usa cuando
 * el backend confirma el guardado sin devolver el documento: el próximo PUT
 * vuelve a ser parcial en vez de reenviar todo.
 * @param {object} state
 * @returns {object} copia con baseline refrescado
 */
export function markSaved(state) {
  return { ...state, baseline: snapshot(state) };
}

/**
 * Payload del POST/PUT. Con `isNew` (o sin baseline) viaja el documento
 * completo; con baseline, solo lo que cambió respecto del GET — así un PUT no
 * pisa secciones que el usuario nunca abrió.
 *
 * `provider.api_token` viaja SOLO si el usuario tipeó uno: ausente = el backend
 * conserva el token guardado.
 *
 * `allowProvider: false` (el rol del llamador no administra el proveedor) saca
 * la sección `provider` ENTERA del body: un PUT parcial sin esa clave no la
 * toca, y mandarla —aunque sea con lo que el formulario muestra— podría
 * reescribir una configuración que este rol no puede ver ni cambiar.
 *
 * @param {object} state estado del formulario
 * @param {{isNew?: boolean, typedToken?: string, allowProvider?: boolean}} [options]
 * @returns {object} body listo para createAiAgent/updateAiAgent
 */
export function buildAgentPayload(
  state,
  { isNew = false, typedToken = "", allowProvider = true } = {},
) {
  const current = toContract(state);
  const baseline = isNew ? null : state?.baseline || null;
  const payload = {};

  if (!baseline) {
    Object.assign(payload, current);
  } else {
    const base = toContract(baseline);
    IDENTITY_KEYS.forEach((key) => {
      if (current[key] !== base[key]) payload[key] = current[key];
    });
    SECTION_KEYS.forEach((key) => {
      if (JSON.stringify(current[key]) !== JSON.stringify(base[key])) payload[key] = current[key];
    });
  }

  // Sin lista de canales no se toca el alcance (ni al crear: el backend aplica
  // sus defaults).
  if (payload.scope === null) delete payload.scope;

  // `self_learning` viaja solo si el usuario lo cambió (en un alta, si no es el
  // "off" por defecto). La sección behavior se manda entera ante cualquier
  // cambio y el backend la valida con forbidNonWhitelisted: sin esta poda, un
  // backend que todavía no conoce el campo rechazaría con 400 cualquier
  // guardado de horario o mensaje de derivación. Omitirlo es seguro porque el
  // backend solo pisa el valor guardado cuando el campo viene definido.
  if (payload.behavior) {
    const previous = baseline ? toContract(baseline).behavior.self_learning : "off";
    if (payload.behavior.self_learning === previous) {
      const { self_learning: _unchanged, ...behavior } = payload.behavior;
      payload.behavior = behavior;
    }
  }

  if (!allowProvider) {
    delete payload.provider;
    return payload;
  }

  // Un token tipeado solo tiene destino si el agente NO hereda el proveedor
  // del courier: heredando, la credencial que manda es la del courier y este
  // token quedaría guardado sin uso (la vista además esconde el campo).
  const token = current.provider.inherit_tenant ? "" : asString(typedToken).trim();
  if (token) payload.provider = { ...(payload.provider || {}), api_token: token };

  return payload;
}

/**
 * Validación de cliente antes de enviar. Devuelve { campo: mensaje } vacío
 * cuando el formulario está listo; los mensajes son claves i18n (español).
 *
 * Los largos que un input ya acota con maxLength (descripción, avatar,
 * instrucciones) no se repiten acá.
 *
 * `allowProvider: false` salta las reglas del proveedor: sus campos están
 * deshabilitados (el rol no administra el proveedor, o el agente está fuera del
 * alcance del rol), y exigir algo que el usuario no puede corregir dejaría el
 * guardado trabado para siempre.
 *
 * @param {object} state
 * @param {string} [typedToken] token recién tipeado (vacío = no se tocó)
 * @param {{allowProvider?: boolean}} [options]
 * @returns {object}
 */
export function validateAgentForm(state, typedToken = "", { allowProvider = true } = {}) {
  const errors = {};
  const s = state || {};

  const name = asString(s.name).trim();
  if (!name) errors.name = "Escribe un nombre para el agente.";
  else if (name.length > AGENT_NAME_MAX)
    errors.name = "El nombre no puede tener más de 60 caracteres.";

  const assistantName = asString(s.style?.assistant_name).trim();
  if (assistantName.length > AGENT_ASSISTANT_NAME_MAX)
    errors.assistant_name = "El nombre de la asistente no puede tener más de 40 caracteres.";
  else if (!ASSISTANT_NAME_PATTERN.test(assistantName))
    errors.assistant_name =
      "El nombre de la asistente solo admite letras, espacios y los signos . - '";

  const provider = s.provider || {};
  if (allowProvider && provider.inherit_tenant !== true) {
    if (provider.kind === CUSTOM_PROVIDER_ID) {
      const url = asString(provider.base_url).trim();
      if (!url) {
        errors.base_url = "Indica la URL base de tu endpoint.";
      } else if (!/^https:\/\//i.test(url)) {
        errors.base_url = "La URL base debe empezar con https://.";
      } else if (url.includes("@")) {
        errors.base_url = "La URL base no puede incluir usuario ni contraseña.";
      } else if (!isValidUrl(url)) {
        errors.base_url = "La URL base no es una dirección válida.";
      }
    }
    if (!asString(provider.model).trim()) errors.model = "Indica el modelo a usar.";
    // El token tipeado se valida SIEMPRE (aunque haya uno guardado: el nuevo lo
    // reemplaza). Los espacios se revisan después del trim: un token pegado con
    // un salto de línea al final es válido, uno con un espacio en medio no lo es.
    const typed = asString(typedToken).trim();
    if (!typed) {
      if (provider.has_token !== true) {
        errors.token = "Ingresa el token de API de tu proveedor.";
      }
    } else if (typed.length < AGENT_TOKEN_MIN || typed.length > AGENT_TOKEN_MAX) {
      errors.token = "El token debe tener entre 10 y 500 caracteres.";
    } else if (/\s/.test(typed)) {
      errors.token = "El token no puede tener espacios.";
    }
  }

  return errors;
}

// URL parseable y absoluta (además del https:// y la ausencia de '@' que ya se
// revisaron antes).
function isValidUrl(value) {
  try {
    const url = new URL(value);
    return !!url.hostname && !url.username && !url.password;
  } catch {
    return false;
  }
}

/**
 * Capacidades del catálogo que aplican a una audiencia, agrupadas por tipo de
 * permiso. Una capacidad sin la audiencia en su lista NO se ofrece: ofrecer de
 * más es peor que ofrecer de menos (el backend valida ids, no audiencias).
 * @param {object} catalog
 * @param {string} audience
 * @returns {{read: Array, write: Array}}
 */
export function capabilitiesForAudience(catalog, audience) {
  const list = Array.isArray(catalog?.capabilities) ? catalog.capabilities : [];
  const allowed = list.filter(
    (c) =>
      c &&
      typeof c.id === "string" &&
      Array.isArray(c.audiences) &&
      c.audiences.includes(audience),
  );
  return {
    read: allowed.filter((c) => c.kind !== "write"),
    write: allowed.filter((c) => c.kind === "write"),
  };
}
